// Angular Imports
import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';

// Third Party
import jwt_decode from 'jwt-decode';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  map,
  distinctUntilChanged,
  switchMap,
  filter,
} from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { CookiesService } from '@seeplusplus/ngx-utils-cookies-port';

// Snagajob
import { getDomainName } from '@snagajob/utils';

// Libs
import { CollectionResponse } from '@libs/types';
import { APP_CONFIG } from '@libs/configs';

// Self
import {
  getSystemName,
  createUrlToImpersonate,
  isLessThan30MinutesAgo,
  hasSelfSignup,
  isStandardAccount,
  isLegacyECommAccount,
  isEnterpriseAccount,
} from './../shared/functions';
import {
  IAuthentication,
  IAccount,
  IPermissionRequest,
  IEnterAccountRequest,
  ISwitchAccount,
  MarketplacePermission,
  IClaim,
  IPostRequiredDocumentsRequest,
  IPactSafeDocument,
  Ijwt,
  PasswordReset,
} from './authentication.interface';
import { ApiService } from '@libs/services/api-base';
import { v4 as uuid } from 'uuid';

const defaultProfile: MarketplacePermission = {
  id: null,
  claims: [],
};
const defaultAccount: IAccount = {};
const defaultUser: IAuthentication = {
  init: false,
  exp: false,
  loggedIn: false,
  token: null,
  jwt: null,
  access: [],
  requiredDocuments: [],
};

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService extends ApiService {
  private readonly claimsUrlSegment: string = '/edge/claims';
  private readonly accountsUrlSegment: string = '/edge/accounts';
  private readonly loginUrlSegment: string = '/edge/login';
  private readonly usersUrlSegment: string = '/edge/users';

  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private COOKIE_SESSION_EXPIRE = 365; // Cookie itself expires in 1 year
  private COOKIE_SESSION_ID = 'sajJsSession';
  private COOKIE_SESSION_SET = 'sajJsSessionSet';

  user$ = new BehaviorSubject<IAuthentication>({ ...defaultUser });

  account$ = new BehaviorSubject<IAccount>({
    ...defaultAccount,
  });

  permission$: BehaviorSubject<MarketplacePermission> =
    new BehaviorSubject<MarketplacePermission>({ ...defaultProfile });

  constructor(
    private http: HttpClient,
    private cookieService: CookiesService,
    @Inject(APP_CONFIG) private appConfig: any,
  ) {
    super();
    // any changes to user we want to write back to local storage
    this.user$
      .pipe(
        distinctUntilChanged(
          (prev: IAuthentication, curr: IAuthentication) =>
            prev.token === curr.token,
        ),
      )
      .subscribe((user: IAuthentication) => {
        this.writeCache(user);
        this.updateSentryUserData(user);
      });

    // update sentry data for auditing!
    this.account$.subscribe((account: IAccount) =>
      this.updateSentryAccountData(account),
    );
  }

  writeCache(user: IAuthentication): void {
    if (localStorage) {
      if (user.loggedIn) {
        localStorage.setItem('snag-marketplace-auth', JSON.stringify(user));
      }
    }
  }

  removeCache(): void {
    if (localStorage) {
      localStorage.removeItem('snag-marketplace-auth');
    }
  }

  restoreFromCache(): Observable<boolean> {
    // don't restore if this is impersonation
    if (
      this.isBrowser &&
      window.location.pathname.toLowerCase() === '/exchange'
    ) {
      return of(true);
    }

    // restore from localstorage if we have local storage available
    if (localStorage) {
      const cachedAuthentication: string = localStorage.getItem(
        'snag-marketplace-auth',
      );
      if (cachedAuthentication) {
        const login: IAuthentication = JSON.parse(cachedAuthentication);

        if (login.loggedIn) {
          // call the validate rout eif we're rehydrating from a cached login
          return this.http
            .get(`${this.loginUrlSegment}/${login.token}/validate`)
            .pipe(
              catchError((error: any) => {
                if (error.status === 401 || error.status === 405) {
                  this.removeCache();
                  this.requestLogin();
                }

                return throwError(error);
              }),
              map(() => {
                this.restoreAccessBasedOnApp(login);
                return true;
              }),
            );
        }
      }
    }

    return of(false);
  }

  getUserAccounts(token: string): Observable<Object> {
    return this.http.get(
      `${this.loginUrlSegment}/user-accounts?Token=${token}&Num=${50}`,
    );
  }

  switchAccount(request: ISwitchAccount): Observable<Object> {
    return this.http.post(`${this.loginUrlSegment}/switch-account`, request);
  }

  switchToDifferentAccount(request: ISwitchAccount): void {
    this.switchAccount(request)
      .pipe(
        switchMap((sut: string) => {
          this.removeCache();

          return this.exchange(sut);
        }),
      )
      .subscribe(() => {
        window.location.reload();
      });
  }

  restoreAccessBasedOnApp(login: IAuthentication): void {
    if (this.appConfig.app === 'ADMIN') {
      this.user$.next(Object.assign(login, { init: true, exp: false }));
    } else {
      this.user$.next(login);
      this.getRequiredDocuments(this.user$.value.jwt.permissionId).subscribe(
        (documents: CollectionResponse<IPactSafeDocument>) => {
          const newUser: IAuthentication = this.user$.getValue();
          if (documents && documents.list.length > 0) {
            documents.list.forEach((document: IPactSafeDocument) => {
              newUser.requiredDocuments.push(document);
            });
          }
          this.user$.next(newUser);
          this.getAccount();
        },
        (error) => {
          this.getAccount();
        },
      );
    }
  }

  getClaims(): Observable<CollectionResponse<IClaim>> {
    return this.http.get<CollectionResponse<IClaim>>(this.claimsUrlSegment);
  }

  exchange(token: string): Observable<any> {
    return this.http.post(`${this.loginUrlSegment}/${token}/exchange`, {}).pipe(
      map((res: string) => {
        this.loadJwt(res);
      }),
    );
  }

  loadJwt(token: string): void {
    const jwt = jwt_decode(token);
    if (this.appConfig.app === 'EMPLOYER') {
      this.externalLogin(jwt, token);
    } else {
      this.internalLogin(jwt, token);
    }
  }

  internalLogin(jwt: any, token: string): void {
    const access: string[] = [];
    if (jwt['can-impersonate'] === 'True') {
      access.push('canImpersonate');
    }

    if (jwt['can-edit-order'] === 'True') {
      access.push('canEditOrder');
    }

    const user: IAuthentication = {
      init: true,
      exp: false,
      loggedIn: true,
      token: token,
      jwt: jwt,
      access: access,
      requiredDocuments: [],
    };

    this.user$.next(user);
  }

  externalLogin(jwt: Ijwt, token: string): void {
    const user: IAuthentication = {
      loggedIn: true,
      accountId: jwt.accountId,
      token,
      jwt,
      requiredDocuments: [],
    };
    // log the user in
    this.user$.next(user);
    this.getRequiredDocuments(this.user$.value.jwt.permissionId).subscribe(
      (documents: CollectionResponse<IPactSafeDocument>) => {
        const newUser: IAuthentication = this.user$.getValue();
        if (documents && documents.list.length > 0) {
          documents.list.forEach((document: IPactSafeDocument) => {
            newUser.requiredDocuments.push(document);
          });
        }
        this.user$.next(newUser);
        this.getAccount();
      },
      (error) => {
        // get information about this customer's account before continuing
        this.getAccount();
      },
    );
  }

  hasSelfSignup() {
    return hasSelfSignup(this.account$.getValue());
  }

  isStandardAccount() {
    return isStandardAccount(this.account$.getValue());
  }

  isLegacyECommAccount() {
    return isLegacyECommAccount(this.account$.getValue());
  }

  isEnterpriseAccount() {
    return isEnterpriseAccount(this.account$.getValue());
  }

  setProfile(user: IAuthentication): void {
    if (user.jwt.prt === 'sajcorp' && !user.jwt.permissionId) {
      // not ideal but a quick temp fix for enter account to get all the claims
      const tempEnterAccountProfile: MarketplacePermission = {
        id: user.jwt.prn,
        accountId: user.jwt.accountId,
        emailAddress: user.jwt.prn,
        hasAllLocations: true,
      };

      this.http
        .get(this.claimsUrlSegment)
        .toPromise()
        .then((claims: CollectionResponse<IClaim>) => {
          const claimNames: string[] = claims.list.map((x: IClaim) => x.name);
          Object.assign(tempEnterAccountProfile, { claims: claimNames });
          this.permission$.next(tempEnterAccountProfile);
        });
    } else {
      this.http
        .get('/edge/users/' + user.jwt.permissionId)
        .toPromise()
        .then((permission: MarketplacePermission) => {
          this.permission$.next(permission);
        });
    }
  }

  getAccount(): void {
    this.user$
      .pipe(
        filter((user) => !!user.accountId),
        switchMap((user) => {
          this.setProfile(user);
          return this.http.get(`${this.accountsUrlSegment}/${user.accountId}`);
        }),
      )
      .subscribe((account: IAccount) => {
        if (!account.accountType) {
          account.accountType = 'Subscription';
        }
        this.account$.next(account);
      });
  }

  requestLogin(path: string = '/', force: boolean = false): void {
    // don't request login if a token swap fails
    if (
      !force &&
      this.isBrowser &&
      window.location.href.indexOf('auth/token') > 0
    ) {
      return;
    }

    if (this.isBrowser && window) {
      // we wont have this at compile time since the container is deployed reusably so grab
      // the domain from the domain of the window
      let domain: string = getDomainName(window.location.hostname);
      let system: string = getSystemName(window.location.hostname);

      // force prod to use snagajob.com rather than snagprod.com
      domain = domain === 'snagprod.com' ? 'snagajob.com' : domain;

      // for local development we need to tell the login server in this environment
      // to send back to our machine w/ the SUT instead of the deployed endpoint
      if (domain === 'localhost') {
        system = 'local4200';
        domain = 'snagdev.com';
      }

      // send to one login and come back when you're ready to exchange SUT with our auth
      window.location.href = `https://login.${domain}/redirect?system=${system}&path=${encodeURIComponent(path)}`;
    }
  }

  logout(): Observable<null> {
    const user: IAuthentication = this.user$.getValue();

    // Attempt to clear the sentry meta-data.
    try {
      this.updateSentryAccountData(null);
      this.updateSentryUserData(null);
    } catch {
      // Do nothing, we don't want to break logout. Not a critical failure.
    }

    return this.http
      .delete(`${this.loginUrlSegment}/${user.token}`)
      .pipe(map(() => null));
  }

  error(error: any): Observable<any> {
    return throwError(error);
  }

  canEditAccount(): boolean {
    const account: IAccount = this.account$.getValue();

    return account == null ? false : this.canEditPlatform(account.platform);
  }

  canEditPlatform(platform: string): boolean {
    return platform === 'Marketplace' || platform === 'PeopleMatter';
  }

  hasAccess(action: string): boolean {
    if (!this.user$.getValue().access) {
      return false;
    }

    return this.user$.getValue().access.indexOf(action) > -1;
  }

  hasClaims(claims: string[]): boolean {
    if (claims) {
      const hasPermission: boolean = this.permission$
        .getValue()
        .claims.some((claim: string) => claims.includes(claim));
      return hasPermission;
    } else {
      return false;
    }
  }

  hasRequireClaim(claim: string): boolean {
    const appName: string = this.appConfig.app;
    if (appName === 'ADMIN') {
      return true;
    }

    return this.permission$.getValue().claims.indexOf(claim) > -1;
  }

  hasRequirePermissionsForLocations(): boolean {
    return this.permission$.getValue()?.hasAllLocations;
  }

  isImpersonatedUser(): boolean {
    const user: IAuthentication = this.user$.value;

    if (!user.loggedIn) {
      return false;
    }

    return user.jwt.prt === 'sajcorp';
  }

  getPermissionId(): string {
    const user: IAuthentication = this.user$.value;

    return user.jwt.permissionId;
  }

  hasPermissionId(): boolean {
    return !!this.user$?.value?.jwt?.permissionId;
  }

  updateSentryAccountData(account: IAccount): void {
    if (account?.id) {
      Sentry.setContext('account-context', {
        id: account.id,
        'account-type': account.accountType || 'unknown',
        platform: account.platform || ' unknown',
      });
    } else {
      Sentry.setContext('account-context', null);
    }
  }

  updateSentryUserData(user: IAuthentication): void {
    const prt = user?.jwt?.prt;

    if (prt) {
      let prn = user?.jwt?.prn;
      if (prt === 'sajcorp') {
        // don't log prn for internal because it's PII
        prn = null;
      }
      Sentry.setUser({
        id: prn,
        type: prt || 'unknown',
      });
    } else {
      Sentry.setUser(null);
    }
  }

  // ADMIN (admin.snag{env}.com) use only
  enterAccount(accountId: string): void {
    const request = { accountId: accountId } as IEnterAccountRequest;
    this.http
      .post(`${this.loginUrlSegment}/enter-account`, request)
      .subscribe((jwt: string) => {
        window.open(
          `${createUrlToImpersonate(this.appConfig.marketplaceUrl)}/exchange?jwt=${jwt}`,
          '_blank',
        );
      });
  }

  impersonateAndNavigate(permissionId: string): void {
    const request = { permissionId: permissionId } as IPermissionRequest;
    this.http
      .post(`${this.loginUrlSegment}/impersonate`, request)
      .subscribe((jwt: string) => {
        window.open(
          `${createUrlToImpersonate(this.appConfig.marketplaceUrl)}/exchange?jwt=${jwt}`,
          '_blank',
        );
      });
  }

  postRequiredDocuments(
    userId: string,
    payload: IPostRequiredDocumentsRequest,
  ) {
    return this.http.post(
      `${this.usersUrlSegment}/${userId}/required-documents`,
      payload,
      { observe: 'response' },
    );
  }

  getRequiredDocuments(
    userId: string,
  ): Observable<CollectionResponse<IPactSafeDocument>> {
    return this.http.get<CollectionResponse<IPactSafeDocument>>(
      `${this.usersUrlSegment}/${userId}/required-documents`,
    );
  }

  clearRequiredDocuments() {
    const user: IAuthentication = this.user$.getValue();
    user.requiredDocuments = [];
    this.user$.next({ ...user });
  }

  putChangePassword(
    userId: string,
    payload: PasswordReset,
  ): Observable<PasswordReset> {
    return this.catchValidationError(
      this.http.put<PasswordReset>(
        this.usersUrlSegment + '/' + userId + '/password-reset',
        payload,
      ),
    );
  }

  getValidQueryParametersForLoginPath(path: string): string {
    if (!path) {
      path = '';
    }

    const validQueryParamList = ['ref'];
    const queryParams = path.split('?')?.[1] ?? path;

    const params = new URLSearchParams(queryParams);

    const normalize = (s?: string) => (s ? `?${s}` : '');

    return normalize(
      new URLSearchParams(
        validQueryParamList
          .filter((q) => params.has(q))
          .map((param) => [param, params.get(param)]),
      ).toString(),
    );
  }

  getSessionId(): string {
    // Both the session cookies should expire in 1 year
    const expires = new Date();
    expires.setDate(expires.getDate() + this.COOKIE_SESSION_EXPIRE);
    let sessionId: string;

    // If there is a sessionId and the value of the COOKIE_SESSION_SET is less than 30 mins use the existing sessionId if not generate a new one
    if (
      this.cookieService.get(this.COOKIE_SESSION_ID) &&
      isLessThan30MinutesAgo(
        Number(this.cookieService.get(this.COOKIE_SESSION_SET)),
      )
    ) {
      sessionId = this.cookieService.get(this.COOKIE_SESSION_ID);
    } else {
      sessionId = uuid();
    }

    const time = Math.floor(Date.now() / 1000).toString();
    // Set the cookies, these continue to update when the user is active on the site, once a user
    // is no longer active it should expire in 30 mins
    this.cookieService.put(this.COOKIE_SESSION_ID, sessionId, { expires });
    this.cookieService.put(this.COOKIE_SESSION_SET, time, { expires });
    return sessionId;
  }
}
